import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import {
  AutomationDialogService,
  AutomationsFacade,
} from '@principle-theorem/ng-automations';
import {
  Appointment,
  IRescheduleAutomationListData,
  TreatmentStep,
  type AutomationEntity,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  TemplateScope,
  type IAppointmentScopeData,
  type ITreatmentStep,
  AutomationParentScope,
  AutomationContext,
} from '@principle-theorem/principle-core/interfaces';
import { isWithRef, snapshot, type WithRef } from '@principle-theorem/shared';
import { ReplaySubject, Subject, type Observable } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pr-appointment-automations',
  templateUrl: './appointment-automations.component.html',
  styleUrls: ['./appointment-automations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppointmentAutomationsComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _appointment$: Observable<WithRef<IAppointment> | undefined>;
  automations$: Observable<IRescheduleAutomationListData[]>;
  hasAutomations$: Observable<boolean>;
  treatmentStep$ = new ReplaySubject<ITreatmentStep | WithRef<ITreatmentStep>>(
    1
  );

  @Input()
  set treatmentStep(step: ITreatmentStep | WithRef<ITreatmentStep>) {
    if (step) {
      this.treatmentStep$.next(step);
    }
  }

  constructor(
    private _automationsFacade: AutomationsFacade,
    private _automationDialog: AutomationDialogService
  ) {
    this.treatmentStep$
      .pipe(
        filter((step): step is WithRef<ITreatmentStep> => isWithRef(step)),
        take(1),
        takeUntil(this._onDestroy$)
      )
      .subscribe(
        (treatmentStep) =>
          void this._automationsFacade.loadTreatmentStepAutomations(
            treatmentStep
          )
      );

    this._appointment$ = this.treatmentStep$.pipe(
      switchMap((treatmentStep) => TreatmentStep.appointment$(treatmentStep))
    );

    this.automations$ = this._automationsFacade.automations$.pipe(
      map((automations) =>
        (automations ?? []).map((automation) => ({
          automation,
          isSelected: false,
          reschedule: false,
        }))
      )
    );
    this.hasAutomations$ = this.automations$.pipe(
      map((automations) => automations.length > 0)
    );
  }

  ngOnDestroy(): void {
    this._automationsFacade.resetState();
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async addTask(): Promise<void> {
    const appointment = await snapshot(this._appointment$);
    if (!appointment?.event) {
      return;
    }
    const treatmentStep = await snapshot(this.treatmentStep$);
    const triggerAfterDate = appointment
      ? Appointment.getCompletedAt(appointment)
      : undefined;

    this._automationsFacade.addTask(
      appointment.event,
      { parentScope: AutomationParentScope.TreatmentStep },
      AutomationContext.Appointment,
      isWithRef(treatmentStep) ? treatmentStep.ref : undefined,
      triggerAfterDate
    );
  }

  async addNotification(): Promise<void> {
    const appointment = await snapshot(this._appointment$);
    if (!appointment?.event) {
      return;
    }
    const treatmentStep = await snapshot(this.treatmentStep$);
    const triggerAfterDate = appointment
      ? Appointment.getCompletedAt(appointment)
      : undefined;
    // TODO: https://app.clickup.com/t/860qzurhu
    const scopeData: IAppointmentScopeData = {} as IAppointmentScopeData;
    this._automationsFacade.addNotification(
      TemplateScope.Appointment,
      scopeData,
      appointment.event,
      { parentScope: AutomationParentScope.TreatmentStep },
      isWithRef(treatmentStep) ? treatmentStep.ref : undefined,
      triggerAfterDate
    );
  }

  async addFormIssue(): Promise<void> {
    const appointment = await snapshot(this._appointment$);
    if (!appointment?.event) {
      return;
    }
    const treatmentStep = await snapshot(this.treatmentStep$);
    const triggerAfterDate = appointment
      ? Appointment.getCompletedAt(appointment)
      : undefined;

    this._automationsFacade.addAutomatedFormIssue(
      appointment.event,
      isWithRef(treatmentStep) ? treatmentStep.ref : undefined,
      triggerAfterDate
    );
  }

  async addFromTemplate(): Promise<void> {
    const appointment = await snapshot(this._appointment$);
    if (!appointment?.event) {
      return;
    }
    const treatmentStep = await snapshot(this.treatmentStep$);
    const triggerAfterDate = Appointment.getCompletedAt(appointment);

    this._automationsFacade.addFromTemplate(
      TemplateScope.Appointment,
      appointment.event,
      { parentScope: AutomationParentScope.TreatmentStep },
      isWithRef(treatmentStep) ? treatmentStep.ref : undefined,
      triggerAfterDate
    );
  }

  async edit(automation: AutomationEntity): Promise<void> {
    const appointment = await snapshot(this._appointment$);
    if (!appointment?.event) {
      return;
    }
    this._automationsFacade.editAutomation(
      automation,
      appointment.event,
      AutomationContext.Appointment
    );
  }

  cancel(automation: AutomationEntity): void {
    this._automationsFacade.cancelOne(automation);
  }

  async restore(automation: AutomationEntity): Promise<void> {
    await this._automationsFacade.restoreAutomation(automation);
  }

  resend(automation: AutomationEntity): void {
    this._automationsFacade.resend(automation);
  }

  async preview(automation: AutomationEntity): Promise<void> {
    await this._automationDialog.openPreviewDialog(automation);
  }
}
